import { cn } from "@/lib/utils";
import { Moon, Sun, Info, HelpCircle, FolderOpen, LogIn, LogOut, ShieldCheck, Users, History } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRoles";

interface AppHeaderProps {
  jurisdiction: 'canada' | 'usa';
  mode: 'advisor' | 'client';
  theme: 'light' | 'dark';
  onJurisdictionChange: (j: 'canada' | 'usa') => void;
  onModeChange: (m: 'advisor' | 'client') => void;
  onToggleTheme: () => void;
}

export function AppHeader({ jurisdiction, mode, theme, onJurisdictionChange, onModeChange, onToggleTheme }: AppHeaderProps) {
  const { user, signOut } = useAuth();
  const { isAdmin, isAdvisor } = useRoles();
  return (
    <header className="bg-primary border-b border-border no-print">
      <div className="container mx-auto flex items-center justify-between h-16 px-4 lg:px-8">
        <h1 className="text-base sm:text-lg font-semibold text-primary-foreground tracking-tight">
          Family Policy Loan Planner™
        </h1>
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            <Link to="/about" className="px-2 py-1.5 text-xs font-medium text-primary-foreground/60 hover:text-primary-foreground transition-colors flex items-center gap-1">
              <Info className="h-3 w-3" /> About
            </Link>
            <Link to="/faq" className="px-2 py-1.5 text-xs font-medium text-primary-foreground/60 hover:text-primary-foreground transition-colors flex items-center gap-1">
              <HelpCircle className="h-3 w-3" /> FAQ
            </Link>
            {user && (
              <Link to="/scenarios" className="px-2 py-1.5 text-xs font-medium text-primary-foreground/60 hover:text-primary-foreground transition-colors flex items-center gap-1">
                <FolderOpen className="h-3 w-3" /> My Scenarios
              </Link>
            )}
            {user && (
              <Link to="/timeline" className="px-2 py-1.5 text-xs font-medium text-primary-foreground/60 hover:text-primary-foreground transition-colors flex items-center gap-1">
                <History className="h-3 w-3" /> Timeline
              </Link>
            )}
            {user && isAdvisor && (
              <Link to="/advisor" className="px-2 py-1.5 text-xs font-medium text-primary-foreground/60 hover:text-primary-foreground transition-colors flex items-center gap-1">
                <Users className="h-3 w-3" /> Advisor
              </Link>
            )}
            {user && isAdmin && (
              <Link to="/admin" className="px-2 py-1.5 text-xs font-medium text-primary-foreground/60 hover:text-primary-foreground transition-colors flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Admin
              </Link>
            )}
          </div>

          {/* Jurisdiction Selector */}
          <div className="hidden sm:flex rounded-md overflow-hidden border border-primary-foreground/20">
            <button
              onClick={() => onJurisdictionChange('canada')}
              className={cn(
                "px-3 py-1.5 text-xs font-medium transition-colors duration-200",
                jurisdiction === 'canada'
                  ? "bg-accent text-accent-foreground"
                  : "bg-primary text-primary-foreground/60 hover:text-primary-foreground"
              )}
            >
              Canada
            </button>
            <button
              onClick={() => onJurisdictionChange('usa')}
              className={cn(
                "px-3 py-1.5 text-xs font-medium transition-colors duration-200",
                jurisdiction === 'usa'
                  ? "bg-accent text-accent-foreground"
                  : "bg-primary text-primary-foreground/60 hover:text-primary-foreground"
              )}
            >
              United States
            </button>
          </div>

          {/* Mode Toggle */}
          <div className="hidden sm:flex rounded-md overflow-hidden border border-primary-foreground/20">
            <button
              onClick={() => onModeChange('advisor')}
              className={cn(
                "px-3 py-1.5 text-xs font-medium transition-colors duration-200",
                mode === 'advisor'
                  ? "bg-accent text-accent-foreground"
                  : "bg-primary text-primary-foreground/60 hover:text-primary-foreground"
              )}
            >
              Advisor
            </button>
            <button
              onClick={() => onModeChange('client')}
              className={cn(
                "px-3 py-1.5 text-xs font-medium transition-colors duration-200",
                mode === 'client'
                  ? "bg-accent text-accent-foreground"
                  : "bg-primary text-primary-foreground/60 hover:text-primary-foreground"
              )}
            >
              Client
            </button>
          </div>

          {/* Dark Mode Toggle Switch */}
          <div className="flex items-center gap-2">
            <Sun className="h-3.5 w-3.5 text-primary-foreground/60" />
            <Switch
              checked={theme === 'dark'}
              onCheckedChange={onToggleTheme}
              className="data-[state=checked]:bg-accent data-[state=unchecked]:bg-primary-foreground/20"
              aria-label="Toggle dark mode"
            />
            <Moon className="h-3.5 w-3.5 text-primary-foreground/60" />
            <Label className="text-xs text-primary-foreground/70 hidden sm:inline cursor-pointer" onClick={onToggleTheme}>
              Dark Mode
            </Label>
          </div>

          {user ? (
            <button
              onClick={() => signOut()}
              className="text-xs font-medium text-primary-foreground/70 hover:text-primary-foreground flex items-center gap-1"
              title={user.email ?? "Sign out"}
            >
              <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          ) : (
            <Link
              to="/auth"
              className="text-xs font-medium text-primary-foreground/70 hover:text-primary-foreground flex items-center gap-1"
            >
              <LogIn className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Sign in</span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
